import type { StockItem, StockLocation } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  CheckboxField,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Tr,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Boxes, MapPin, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { outletsQueryOptions } from '@/features/pos/queries';
import { quantityLabel } from '@/features/stock/labels';
import { stockItemsQueryOptions, stockLocationsQueryOptions } from '@/features/stock/queries';
import { ItemDrawer, LocationDrawer } from '@/features/stock/StockDrawers';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/stock/items')({ component: StockItemsPage });

/** Articles de stock et lieux de stockage de l'établissement (ADR-020). */
function StockItemsPage() {
  const { t, i18n } = useTranslation(['stock', 'common', 'errors']);
  const session = useSession();
  const { propertyId } = useCurrentProperty();
  const canRead = session.can('stock.read');
  const canManage = session.can('stock.manage');
  const [showInactive, setShowInactive] = useState(false);
  const [itemDrawer, setItemDrawer] = useState<{ open: boolean; item: StockItem | null }>({
    open: false,
    item: null,
  });
  const [locationDrawer, setLocationDrawer] = useState<{
    open: boolean;
    location: StockLocation | null;
  }>({
    open: false,
    location: null,
  });
  const items = useQuery(
    stockItemsQueryOptions({ propertyId, includeInactive: showInactive }, canRead),
  );
  const locations = useQuery(stockLocationsQueryOptions(propertyId, canRead, showInactive));
  const outlets = useQuery(outletsQueryOptions(propertyId, canRead && session.hasModule('pos')));
  const outletName = (id: string | null) =>
    outlets.data?.find((outlet) => outlet.id === id)?.name ?? '—';

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('stock:items.title')}
        subtitle={t('stock:items.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('stock:title'), link: <Link to="/stock">{t('stock:title')}</Link> },
          { label: t('stock:items.title') },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <CheckboxField
              label={t('stock:items.showInactive')}
              checked={showInactive}
              onCheckedChange={(checked) => setShowInactive(checked === true)}
            />
            {canManage && propertyId ? (
              <>
                <Button
                  variant="secondary"
                  onClick={() => setLocationDrawer({ open: true, location: null })}
                >
                  <MapPin className="ico" aria-hidden="true" />
                  {t('stock:items.newLocation')}
                </Button>
                <Button variant="primary" onClick={() => setItemDrawer({ open: true, item: null })}>
                  <Plus className="ico" aria-hidden="true" />
                  {t('stock:items.newItem')}
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      <Card>
        <CardHeader
          title={t('stock:items.itemsCard')}
          icon={<Boxes className="ico ico-16" aria-hidden="true" />}
        />
        <CardBody density="flush">
          {items.isPending ? (
            <TableSkeleton rows={5} columns={7} label={t('common:states.loading')} />
          ) : items.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(items.error, t)}
              traceId={errorTraceId(items.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void items.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : items.data.length === 0 ? (
            <EmptyState
              icon={<Boxes className="ico" aria-hidden="true" />}
              title={t('stock:items.emptyItems')}
              description={t('stock:items.emptyItemsHint')}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('stock:items.columns.code')}</Th>
                    <Th>{t('stock:items.columns.name')}</Th>
                    <Th>{t('stock:items.columns.category')}</Th>
                    <Th>{t('stock:items.columns.unit')}</Th>
                    <Th>{t('stock:items.columns.package')}</Th>
                    <Th align="right">{t('stock:items.columns.min')}</Th>
                    <Th align="right">{t('stock:items.columns.cost')}</Th>
                    <Th>{t('stock:items.columns.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {items.data.map((item) => (
                    <Tr
                      key={item.id}
                      onClick={() => canManage && setItemDrawer({ open: true, item })}
                      style={canManage ? { cursor: 'pointer' } : undefined}
                    >
                      <Td className="mono">{item.code}</Td>
                      <Td>
                        <strong>{item.name}</strong>
                      </Td>
                      <Td>{t(`stock:category.${item.category}`)}</Td>
                      <Td>{t(`stock:unit.${item.unit}`)}</Td>
                      <Td>
                        {item.packageLabel ?? '—'}
                        {item.packageQuantity !== 1 ? (
                          <span className="t-12 muted">
                            {' '}
                            · {quantityLabel(item.packageQuantity, i18n.language)}{' '}
                            {t(`stock:unit.${item.unit}`)}
                          </span>
                        ) : null}
                      </Td>
                      <Td numeric align="right">
                        {item.minQuantity === null
                          ? '—'
                          : quantityLabel(item.minQuantity, i18n.language)}
                      </Td>
                      <Td numeric align="right">
                        {formatAmount(item.averageUnitCost, i18n.language, item.currencyCode)}
                      </Td>
                      <Td>
                        <Badge tone={item.isActive ? 'green' : 'neutral'}>
                          {item.isActive ? t('stock:items.active') : t('stock:items.inactive')}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={t('stock:items.locationsCard')}
          icon={<MapPin className="ico ico-16" aria-hidden="true" />}
        />
        <CardBody density="flush">
          {locations.isPending ? (
            <TableSkeleton rows={3} columns={5} label={t('common:states.loading')} />
          ) : locations.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(locations.error, t)}
              traceId={errorTraceId(locations.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void locations.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : locations.data.length === 0 ? (
            <EmptyState
              icon={<MapPin className="ico" aria-hidden="true" />}
              title={t('stock:items.emptyLocations')}
              description={t('stock:items.emptyLocationsHint')}
            />
          ) : (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <Th>{t('stock:items.columns.code')}</Th>
                    <Th>{t('stock:items.columns.name')}</Th>
                    <Th>{t('stock:items.columns.outlet')}</Th>
                    <Th>{t('stock:items.columns.default')}</Th>
                    <Th>{t('stock:items.columns.status')}</Th>
                  </tr>
                </thead>
                <tbody>
                  {locations.data.map((location) => (
                    <Tr
                      key={location.id}
                      onClick={() => canManage && setLocationDrawer({ open: true, location })}
                      style={canManage ? { cursor: 'pointer' } : undefined}
                    >
                      <Td className="mono">{location.code}</Td>
                      <Td>
                        <strong>{location.name}</strong>
                      </Td>
                      <Td>{location.outletId ? outletName(location.outletId) : '—'}</Td>
                      <Td>
                        {location.isDefault ? (
                          <Badge tone="violet">{t('stock:items.isDefault')}</Badge>
                        ) : null}
                      </Td>
                      <Td>
                        <Badge tone={location.isActive ? 'green' : 'neutral'}>
                          {location.isActive ? t('stock:items.active') : t('stock:items.inactive')}
                        </Badge>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}
        </CardBody>
      </Card>

      {canManage && propertyId ? (
        <>
          <ItemDrawer
            open={itemDrawer.open}
            onOpenChange={(open) => setItemDrawer((current) => ({ ...current, open }))}
            propertyId={propertyId}
            item={itemDrawer.item}
          />
          <LocationDrawer
            open={locationDrawer.open}
            onOpenChange={(open) => setLocationDrawer((current) => ({ ...current, open }))}
            propertyId={propertyId}
            outlets={outlets.data ?? []}
            location={locationDrawer.location}
          />
        </>
      ) : null}
    </Container>
  );
}
